type LoopFrameRequest = (cb: FrameRequestCallback) => number
type LoopFrameCancel = (handle: number) => void

// interface LoopOptions {}
interface LoopState {
  first: boolean
  started: boolean
  running: boolean
  panic: boolean
}

type LoopBeginCallback = (timestamp: number, dt: number) => void
type LoopUpdateCallback = (timestep: number) => void
type LoopRenderCallback = (interp: number, dt: number) => void
type LoopEndCallback = (fps: number, panic: boolean) => void

interface LoopCustomUpdate {
  frameDelta: number
  timestep: number
  cb: LoopUpdateCallback
}

const noop = () => {}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _global: any =
  // prettier-ignore
  typeof globalThis !== 'undefined'
    ? globalThis
    : typeof window !== 'undefined'
      ? window
      // : typeof global !== 'undefined'
      //   ? global
        : typeof self !== 'undefined'
          ? self
          : {}

export class Loop {
  private hasRaf = 'requestAnimationFrame' in _global

  /**
   * Update
   */
  // Time unit for updates.
  private updateInterval = 1000
  // Client vs server update rate.
  private updateFps = this.hasRaf ? 60 : 45
  // Slower or faster factor.
  private updateFactor = 1
  // How many updates have been performed per frame.
  private numUpdateSteps = 0
  // Amount of time for each update simulation.
  private updateTimeStep = this.updateInterval / this.updateFps
  // Custom updates
  private customUpdates: Map<string, LoopCustomUpdate> = new Map()

  // Frame
  // Frame time that hasn't been simulated yet.
  private frameDelta = 0
  // Last frame timesamp.
  private lastFrameTimeMs = 0
  // Minimum time between frames.
  private minFrameDelay = 0
  private frameID = -1
  // No requestAnimationFrame on Node.js
  // https://github.com/underscorediscovery/realtime-multiplayer-in-html5
  private frameRequest: LoopFrameRequest = this.hasRaf
    ? _global.requestAnimationFrame
    : (function () {
        let lastTimestamp = Date.now()
        let now: number
        let timeout: number

        return function (this: Loop, callback) {
          now = Date.now()
          // The next frame should run no sooner than the simulation allows,
          // but as soon as possible if the current frame has already taken
          // more time to run than is simulated in one timestep.
          timeout = Math.max(0, this.updateTimeStep - (now - lastTimestamp))
          lastTimestamp = now + timeout

          return _global.setTimeout(() => {
            callback(now + timeout)
          }, timeout)
        }
      })()

  private frameCancel: LoopFrameCancel = this.hasRaf
    ? _global.cancelAnimationFrame
    : _global.clearTimeout

  // FPS
  // Fps average.
  private _fps = this.updateFps
  // "decay" parameter for fps average.
  private fpsDecay = 0.9
  // Fps average refresh interval.
  private fpsUpdateInterval = 1000
  // Last fps caluclation timesamp.
  private lastFpsUpdate = 0
  // How many frames since last fps calculation.
  private framesSinceLastFpsUpdate = 0

  // Timings
  private beginning = performance.now()

  // State
  private state: LoopState = {
    first: true, // First frame, no start.
    started: false, // Start has been called.
    running: false, // Loop is running.
    panic: false, // Too many updates in one frame.
  }

  // Callbacks
  private begin: LoopBeginCallback | undefined // Beginning of the main loop.
  private update: LoopUpdateCallback = noop // Run updates (physics, AI, …).
  private render: LoopRenderCallback = noop // Render the screen.
  private end: LoopEndCallback | undefined // End of the main loop.

  // Options
  // private options: LoopOptions = {}

  // constructor(options?: Partial<LoopOptions>) {
  constructor() {
    // Manage options…
    // this.options = {
    //   ...this.options,
    //   ...options,
    // }

    // Bindings
    this.frameRequest = this.frameRequest.bind(this.hasRaf ? _global : this)
    this.frameCancel = this.frameCancel.bind(this.hasRaf ? _global : _global)
    this.frame = this.frame.bind(this)
  }

  /**
   * Main loop
   * @param timestamp - Timestamp of the current frame. The time the page started loading or Unix epoch.
   * @returns
   */
  private frame(timestamp: DOMHighResTimeStamp) {
    // Run the loop ASAP so it can be canceled during the current frame.
    this.frameID = this.frameRequest(this.frame)

    // Throttle the frame rate.
    if (timestamp < this.lastFrameTimeMs + this.minFrameDelay) {
      return
    }

    // Frame delta.
    const delta = timestamp - this.lastFrameTimeMs

    // Not yet simulated time.
    this.frameDelta += delta

    // Custom updates
    if (this.customUpdates.size > 0) {
      for (const [, step] of this.customUpdates) {
        step.frameDelta += delta
      }
    }

    // Save it for next frame.
    this.lastFrameTimeMs = timestamp

    // Begin callback, if any.
    // Run time independent code.
    this.begin?.(timestamp, this.frameDelta)

    if (this.state.running) {
      // Fps calculation.
      if (timestamp > this.lastFpsUpdate + this.fpsUpdateInterval) {
        // With decay, more recent seconds affect the estimated frame rate more than older seconds.
        this._fps =
          (this.fpsDecay * this.framesSinceLastFpsUpdate * 1000) /
            (timestamp - this.lastFpsUpdate) +
          (1 - this.fpsDecay) * this._fps

        // Reset
        this.lastFpsUpdate = timestamp
        this.framesSinceLastFpsUpdate = 0
      }

      // Count this frame as a new frame.
      this.framesSinceLastFpsUpdate += 1

      this.numUpdateSteps = 0

      while (this.frameDelta >= this.updateTimeStep) {
        this.update(this.updateTimeStep * this.updateFactor)
        this.frameDelta -= this.updateTimeStep

        /**
         * Check if we run the loop too many times (update duration > `updateTimeStep`).
         *
         * Why 240 ? For any sane value of updateTimeStep, 240 updates will simulate at least one second.
         * It will simulate four seconds with the default value of simulationTimestep
         * and Safari notifies users that the script is taking too long to run if it takes more than 5s.
         */
        if (++this.numUpdateSteps >= 240) {
          this.state.panic = true
          break
        }
      }

      // Custom updates.
      if (this.customUpdates.size > 0) {
        for (const [, step] of this.customUpdates) {
          if (step.frameDelta >= step.timestep) {
            step.cb(step.timestep * this.updateFactor)
            step.frameDelta -= step.timestep
          }
        }
      }
    }

    /**
     * Render the screen.
     * We use interpolation because frame rate and update rate are not synced.
     *   update() timesteps:  |  |  |  |  |  |  |  |  |
     *   render() calls:      |   |   |   |   |   |   |
     * New position = old position + (last updated position - old position) * interp.
     */

    this.render(this.frameDelta / this.updateTimeStep, delta)

    // End callback, if any.
    // Run time independent code.
    this.end?.(this._fps, this.state.panic)
  }

  /**
   * Accessors
   */
  public get fps() {
    return this._fps
  }

  public get fpsMax() {
    return Math.round(1000 / (this.minFrameDelay + 1))
  }

  public set fpsMax(fps: number) {
    if (!fps) {
      fps = Infinity
    }
    if (fps === 0) {
      this.stop()
    } else {
      this.minFrameDelay = 1000 / fps - 1
    }
  }

  public get multiplier() {
    return this.updateFactor
  }

  public set multiplier(multiplier: number) {
    this.updateFactor = multiplier
  }

  public get elapsed() {
    return (performance.now() - this.beginning) / 1000
  }

  /**
   * Add custom fixed update callback.
   * Fps value must be smaller than AND multiplier of `updateFps`.
   * @param fps - Fps value.
   * @param cb - Callback.
   * @returns Loop instance.
   */
  public addCustomUpdate(name: string, fps: number, cb: LoopUpdateCallback) {
    if (fps >= this.updateFps) {
      console.warn(
        `[Loop] Fps value (${fps}) must be smaller than updateFps (${this.updateFps})`
      )

      return this
    }

    if (!Number.isInteger(this.updateFps / fps)) {
      console.warn(
        `[Loop] updateFps (${this.updateFps}) must be divisible by fps value (${this.updateFps} / ${fps} is not an integer)`
      )

      return this
    }

    this.customUpdates.set(name, {
      frameDelta: 0,
      timestep: this.updateInterval / fps,
      cb,
    })

    return this
  }

  /**
   * Begin callback, time independent.
   * @param fn - Callback.
   * @returns Loop instance.
   */
  public setBegin(fn: LoopBeginCallback | undefined) {
    this.begin = fn

    return this
  }

  /**
   * Update callback, fast and fixed.
   * @param fn - Callback.
   * @returns Loop instance.
   */
  public setUpdate(fn: LoopUpdateCallback) {
    this.update = fn

    return this
  }

  /**
   * Render callback, draw he screen.
   * @param fn - Callback.
   * @returns Loop instance.
   */
  public setRender(fn: LoopRenderCallback) {
    this.render = fn

    return this
  }

  /**
   * End callback, time independent.
   * @param fn - Callback.
   * @returns Loop instance.
   */
  public setEnd(fn: LoopEndCallback | undefined) {
    this.end = fn

    return this
  }

  /**
   * Start the loop.
   * @returns Loop instance.
   */
  public start() {
    if (!this.state.started) {
      this.state.started = true

      if (this.state.first) {
        this.state.first = false
        this.beginning = performance.now()
      }

      this.frameID = this.frameRequest(timestamp => {
        this.render(1, 0)

        this.state.running = true
        // Reset
        this.lastFrameTimeMs = timestamp
        this.lastFpsUpdate = timestamp
        this.framesSinceLastFpsUpdate = 0

        this.frameID = this.frameRequest(this.frame)
      })
    }

    return this
  }

  /**
   * Stop the loop.
   * @returns Loop instance.
   */
  public stop() {
    this.frameCancel(this.frameID)
    this.state.started = false
    this.state.running = false

    return this
  }

  /**
   * Dispose of the loop.
   */
  dispose() {
    this.stop()
    this.setBegin(undefined)
    this.setUpdate(noop)
    this.setRender(noop)
    this.setEnd(undefined)
  }
}
